<template>
  <portal-layout>
    <div v-loading="loading" class="order-detail-page">
      <div class="page-head">
        <h2 class="page-title">订单详情</h2>
        <el-button type="text" class="back-btn" @click="goBack">
          <i class="el-icon-arrow-left"></i> 返回我的订单
        </el-button>
      </div>

      <div v-if="!loading && !order.orderNo" class="empty-tip">订单不存在</div>

      <template v-if="order.orderNo">
        <!-- 状态与金额 -->
        <div class="status-card">
          <div class="status-left">
            <div class="status-text" :style="{ color: statusColor(order.status) }">
              {{ statusText(order.status) }}
            </div>
            <div class="status-tip">{{ statusTip(order.status) }}</div>
          </div>
          <div class="status-amount">¥{{ order.totalAmount }}</div>
        </div>

        <!-- 订单基本信息 -->
        <div class="info-card">
          <div class="section-title">订单信息</div>
          <div class="info-row">
            <span class="info-label">订单编号</span>
            <span class="info-value">{{ order.orderNo }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">下单时间</span>
            <span class="info-value">{{ order.createTime || '-' }}</span>
          </div>
          <div v-if="order.payTime" class="info-row">
            <span class="info-label">支付时间</span>
            <span class="info-value">{{ order.payTime }}</span>
          </div>
        </div>

        <!-- 商品信息 -->
        <div class="goods-card">
          <div class="section-title">商品信息</div>
          <div v-for="item in order.orderItems" :key="item.commodityCode" class="goods-item">
            <div class="goods-cover">
              <img :src="item.mainPic" :alt="item.title" />
            </div>
            <div class="goods-info">
              <div class="goods-title">{{ item.title }}</div>
              <div class="goods-subtitle">
                {{ item.courseName }}
                <el-tag size="mini" :type="item.teachingFormat === 'live' ? 'danger' : ''"
                        effect="plain" class="goods-tag">
                  {{ item.teachingFormat === 'live' ? '直播' : '录播' }}
                </el-tag>
              </div>
              <div class="goods-count">
                <span>购买数量：{{ item.quantity }}</span>
                <span>单品课次：{{ item.courseNum }} 节</span>
                <span v-if="item.verificationCount">已核销：{{ item.verificationCount }} 次</span>
              </div>
              <div class="goods-price">
                <span class="price-text">¥{{ item.price }}</span>
                <el-button v-if="canVerification(item)" type="primary" size="mini"
                           @click="openVerification(item)">核销</el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 支付信息 -->
        <div v-if="order.status === '2' || order.status === '3'" class="info-card">
          <div class="section-title">支付信息</div>
          <div class="info-row">
            <span class="info-label">支付方式</span>
            <span class="info-value">{{ payMethodText(order.paymentMethod) }}</span>
          </div>
          <div v-if="order.paymentNo" class="info-row">
            <span class="info-label">支付编号</span>
            <span class="info-value">{{ order.paymentNo }}</span>
          </div>
          <div v-if="order.tradeNo" class="info-row">
            <span class="info-label">交易单号</span>
            <span class="info-value">{{ order.tradeNo }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">退款状态</span>
            <span class="info-value">{{ refundStatusText(order.refundStatus) }}</span>
          </div>
          <div v-if="order.refundAmount && order.refundAmount !== '0.00'" class="info-row">
            <span class="info-label">已退款金额</span>
            <span class="info-value">¥{{ order.refundAmount }}</span>
          </div>
          <div v-if="order.requestRefundAmount && order.requestRefundAmount !== '0.00'" class="info-row">
            <span class="info-label">申请中金额</span>
            <span class="info-value">¥{{ order.requestRefundAmount }}（退款申请中）</span>
          </div>
        </div>

        <!-- 订单操作 -->
        <div class="action-card">
          <span class="action-tip">{{ actionTip }}</span>
          <div class="action-btns">
            <el-button size="small" @click="goContact">联系老师</el-button>
            <el-button v-if="order.status === '1'" size="small" @click="handleCancel">取消订单</el-button>
            <el-button v-if="order.status === '1'" type="primary" size="small" @click="goPay">去支付</el-button>
            <el-button v-if="canRefund" type="warning" size="small" @click="openRefund">申请退款</el-button>
          </div>
        </div>
      </template>

      <!-- 核销弹窗 -->
      <el-dialog title="课程核销" :visible.sync="verificationVisible" width="440px"
                 :close-on-click-modal="false">
        <div class="dialog-info">
          <div class="dialog-row">
            <span class="dialog-label">商品名称</span>
            <span class="dialog-value">{{ currentItem.title || currentItem.courseName }}</span>
          </div>
          <div class="dialog-row">
            <span class="dialog-label">课程数量</span>
            <span class="dialog-value">{{ currentItem.courseNum }} 节课</span>
          </div>
        </div>
        <el-form label-width="80px" size="small" class="dialog-form">
          <el-form-item label="选择小主">
            <el-select v-model="selectedStudentId" placeholder="请选择小主" style="width: 100%">
              <el-option v-for="stu in students" :key="stu.id"
                         :label="stu.name" :value="stu.id"></el-option>
            </el-select>
            <div v-if="students.length === 0" class="dialog-empty">
              暂无小主档案，
              <el-button type="text" @click="goStudent">去添加</el-button>
            </div>
          </el-form-item>
          <el-form-item label="核销数量">
            <span>1</span>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button size="small" @click="verificationVisible = false">取消</el-button>
          <el-button type="primary" size="small" :loading="verificationSubmitting"
                     @click="confirmVerification">确认核销</el-button>
        </div>
      </el-dialog>

      <!-- 申请退款弹窗：门户端只支持整单退款，金额取订单总额 -->
      <el-dialog title="申请退款" :visible.sync="refundVisible" width="440px"
                 :close-on-click-modal="false">
        <div class="dialog-info">
          <div class="dialog-row">
            <span class="dialog-label">订单编号</span>
            <span class="dialog-value">{{ order.orderNo }}</span>
          </div>
          <div class="dialog-row">
            <span class="dialog-label">退款金额</span>
            <span class="dialog-value refund-amount">¥{{ order.totalAmount }}</span>
          </div>
        </div>
        <el-form label-width="80px" size="small" class="dialog-form">
          <el-form-item label="退款原因">
            <el-input v-model="refundReason" type="textarea" :rows="3" maxlength="200"
                      show-word-limit placeholder="请填写退款原因，便于老师审核"></el-input>
          </el-form-item>
        </el-form>
        <div class="refund-tip">提交后需等待老师审核，审核通过后退款将原路退回。</div>
        <div slot="footer">
          <el-button size="small" @click="refundVisible = false">取消</el-button>
          <el-button type="primary" size="small" :loading="refundSubmitting"
                     @click="confirmRefund">提交申请</el-button>
        </div>
      </el-dialog>

      <!-- 微信扫码支付 -->
      <pay-qrcode-dialog ref="payDialog" @success="onPaySuccess" />
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import PayQrcodeDialog from '@/components/PayQrcodeDialog.vue';
import api from '@/common/api';
import storage from '@/common/storage.js';

export default {
  name: 'PortalOrderDetail',
  components: { PortalLayout, PayQrcodeDialog },
  data() {
    return {
      loading: false,
      orderNo: null,
      order: {},
      students: [],
      verificationVisible: false,
      verificationSubmitting: false,
      currentItem: {},
      selectedStudentId: '',
      refundVisible: false,
      refundSubmitting: false,
      refundReason: ''
    }
  },
  computed: {
    // 门户端只支持「未退过款、无申请中退款」的已支付订单整单退款
    // 后端 refundRequest 校验「所选订单行金额之和 == 退款金额」，只有整单退款传全部订单行才能对上
    canRefund() {
      const o = this.order;
      if (!o.orderNo || o.status !== '2') {
        return false;
      }
      return Number(o.refundStatus) === 0 &&
        Number(o.requestRefundAmount || 0) === 0 &&
        Number(o.remainingRefundAmount || 0) > 0;
    },
    actionTip() {
      const map = {
        '1': '订单尚未支付，可点击「去支付」完成付款',
        '2': '如需退款，可提交申请，审核通过后原路退回',
        '3': '订单已完成，如有疑问请联系老师',
        '4': '订单已取消'
      };
      return map[this.order.status] || '如有疑问请联系老师协助';
    }
  },
  created() {
    this.orderNo = this.$route.query.orderNo;
    if (!this.orderNo) {
      this.$message.error('缺少订单编号参数');
      return;
    }
    if (!this.checkLogin()) {
      return;
    }
    this.loadOrderDetail();
    this.loadStudents();
  },
  methods: {
    checkLogin() {
      if (!storage.getString('portalMember')) {
        if (this.$route.name === 'PortalLogin') return false;
        this.$router.push({ name: 'PortalLogin', query: { redirect: this.$route.fullPath } }).catch(() => {});
        return false;
      }
      return true;
    },
    loadOrderDetail() {
      this.loading = true;
      api.order_detail({ orderNo: this.orderNo }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.order = r.data.data;
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.loading = false;
      });
    },
    loadStudents() {
      api.student_list().then((r) => {
        if (r.data.code === "200") {
          // R.ok(List) 走 rows 字段
          this.students = r.data.rows || [];
        }
      }).catch(() => {});
    },
    // 仅已支付状态且订单行仍可核销时展示核销按钮（与后端校验保持一致）
    canVerification(item) {
      return this.order.status === '2' && item.isVerification === true;
    },
    openVerification(item) {
      if (this.students.length === 0) {
        this.$confirm('尚未添加小主档案，核销前需要先添加小主。', '提示', {
          confirmButtonText: '去添加',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.goStudent();
        }).catch(() => {});
        return;
      }
      this.currentItem = item;
      this.selectedStudentId = '';
      this.verificationVisible = true;
    },
    confirmVerification() {
      if (!this.selectedStudentId) {
        this.$message.warning('请选择小主');
        return;
      }
      this.verificationSubmitting = true;
      api.order_verification({
        orderNo: this.order.orderNo,
        orderItemId: this.currentItem.id,
        studentId: this.selectedStudentId
      }).then((r) => {
        if (r.data.code === "200") {
          this.$message.success('核销成功');
          this.verificationVisible = false;
          this.loadOrderDetail();
        } else {
          this.$message.error(r.data.msg || '核销失败');
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.verificationSubmitting = false;
      });
    },
    // 待支付订单继续支付：只传订单号，后端按订单重建金额与订单行
    goPay() {
      this.$refs.payDialog.open({
        orderNo: this.order.orderNo,
        amount: this.order.totalAmount
      });
    },
    onPaySuccess() {
      this.loadOrderDetail();
    },
    openRefund() {
      this.refundReason = '';
      this.refundVisible = true;
    },
    confirmRefund() {
      const reason = (this.refundReason || '').trim();
      if (!reason) {
        this.$message.warning('请填写退款原因');
        return;
      }
      this.refundSubmitting = true;
      api.order_refund_request({
        orderNo: this.order.orderNo,
        // 整单退款：传该订单全部订单行
        orderItemIds: (this.order.orderItems || []).map((t) => t.id),
        refundAmount: this.order.totalAmount,
        refundReason: reason
      }).then((r) => {
        if (r.data.code === "200") {
          this.$message.success('退款申请已提交，请等待老师审核');
          this.refundVisible = false;
          this.loadOrderDetail();
        } else {
          this.$message.error(r.data.msg || '退款申请提交失败');
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.refundSubmitting = false;
      });
    },
    statusText(status) {
      const map = { '1': '待支付', '2': '已支付', '3': '已完成', '4': '已取消' };
      return map[status] || '未知状态';
    },
    statusColor(status) {
      const map = { '1': '#faad14', '2': '#1890ff', '3': '#52c41a', '4': '#bfbfbf' };
      return map[status] || '#bfbfbf';
    },
    statusTip(status) {
      const map = {
        '1': '订单尚未支付，可点击「去支付」完成付款',
        '2': '订单已支付，可对未核销的商品进行核销',
        '3': '订单已完成',
        '4': '订单已取消'
      };
      return map[status] || '';
    },
    payMethodText(method) {
      const map = { wechat: '微信支付', balance: '余额支付', alipay: '支付宝' };
      return map[method] || method || '-';
    },
    refundStatusText(status) {
      const num = Number(status);
      const map = { 0: '无退款', 1: '部分退款', 2: '全部退款' };
      return map[num] || '无退款';
    },
    handleCancel() {
      this.$confirm('确定要取消该订单吗？', '确认取消', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '再想想'
      }).then(() => {
        api.order_cancel({ orderNo: this.order.orderNo }).then((r) => {
          if (r.data.code === "200") {
            this.$message.success('取消成功');
            this.loadOrderDetail();
          } else {
            this.$message.error(r.data.msg || '取消失败');
          }
        }).catch(() => {});
      }).catch(() => {});
    },
    goContact() {
      this.$router.push('/portal/shop/contact').catch(() => {});
    },
    goStudent() {
      this.$router.push('/portal/user/student').catch(() => {});
    },
    goBack() {
      this.$router.push('/portal/order/list').catch(() => {});
    }
  }
};
</script>
<style scoped>
.order-detail-page {
  min-height: 300px;
}
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
}
.back-btn {
  font-size: 14px;
}
.status-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  padding: 25px 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}
.status-text {
  font-size: 22px;
  font-weight: 500;
}
.status-tip {
  font-size: 13px;
  color: #909399;
  margin-top: 8px;
}
.status-amount {
  font-size: 26px;
  color: #F56C6C;
  font-weight: bold;
}
.info-card,
.goods-card,
.action-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 22px 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}
.section-title {
  font-size: 17px;
  color: #303133;
  font-weight: 500;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 20px;
  margin-bottom: 20px;
}
.info-row {
  display: flex;
  font-size: 14px;
  line-height: 34px;
}
.info-label {
  flex-shrink: 0;
  width: 110px;
  color: #909399;
}
.info-value {
  flex: 1;
  color: #303133;
  word-break: break-all;
}
.goods-item {
  display: flex;
  padding: 18px 0;
  border-bottom: 1px solid #ebeef5;
}
.goods-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.goods-cover {
  flex-shrink: 0;
  width: 110px;
  height: 82px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f5f7fa;
}
.goods-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.goods-info {
  flex: 1;
  min-width: 0;
  padding-left: 20px;
}
.goods-title {
  font-size: 15px;
  color: #303133;
  font-weight: 500;
}
.goods-subtitle {
  font-size: 13px;
  color: #606266;
  margin-top: 8px;
}
.goods-tag {
  margin-left: 8px;
}
.goods-count {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  display: flex;
  gap: 20px;
}
.goods-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}
.price-text {
  font-size: 16px;
  color: #F56C6C;
  font-weight: bold;
}
.action-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.action-tip {
  font-size: 13px;
  color: #909399;
}
.action-btns {
  display: flex;
  gap: 10px;
}
.dialog-info {
  background-color: #fafafa;
  border-radius: 6px;
  padding: 15px 20px;
  margin-bottom: 20px;
}
.dialog-row {
  display: flex;
  font-size: 14px;
  line-height: 28px;
}
.dialog-label {
  flex-shrink: 0;
  width: 80px;
  color: #909399;
}
.dialog-value {
  flex: 1;
  color: #303133;
}
.dialog-empty {
  font-size: 12px;
  color: #909399;
  line-height: 20px;
}
.refund-amount {
  color: #F56C6C;
  font-weight: bold;
}
.refund-tip {
  font-size: 12px;
  color: #909399;
  line-height: 20px;
}
.empty-tip {
  background-color: #fff;
  border-radius: 8px;
  text-align: center;
  color: #909399;
  padding: 60px 0;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
</style>
